import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import SidebarCollapse from "./SidebarCollapse";
import LangButton from "../buttons/LangButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCircle } from "@fortawesome/pro-solid-svg-icons";
import { faBell, faCircleQuestion } from "@fortawesome/pro-light-svg-icons";
import Button from "../buttons/Button";
import NotificationsList from "../notifications/NotificationsList";
import RestaurantAppPreview from "../../components/app/RestaurantAppPreview";
import { useAuth } from "../../hooks/useAuth";
import useLangNavigate from "../../hooks/useLangNavigate";
import useWebSocket from "../../hooks/useWebSocket";
import useApi from "../../hooks/useApi";
import {
  getRestaurant,
  putSwitchRestaurant,
  putSwitchCatering,
} from "../../services/restaurantService";
import { useSelector, useDispatch } from "react-redux";
import {
  stopControllingRestaurant,
  stopControllingCompany,
} from "../../redux/slices/adminSlice";
import { setRestaurantId } from "../../redux/slices/userSlice";
import UserAvatar from "../utils/UserAvatar";
import { isDemoMode } from "../../utils/demoApiInterceptor";

const MobileSidebar = ({
  menuList,
  onClose,
  isClickable,
  className,
  activationDetails = null,
}) => {
  const { logout, isAuthenticated, isRestaurantAdmin, isOffoodAdmin } =
    useAuth();
  const navigate = useLangNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showRestaurantMenu, setShowRestaurantMenu] = useState(false);
  const [isOnOverview, setIsOnOverview] = useState(false);
  const [isRestaurantActive, setIsRestaurantActive] = useState(false);
  const [isCateringActive, setIsCateringActive] = useState(false);
  const prevPathnameRef = useRef(location.pathname);
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const restaurants = useSelector((state) => state.user.restaurants);
  const {
    controlledRestaurantId,
    controlledCompanyId,
    isControllingEntity,
    controlledConsumptionModes,
  } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const { request: fetchRestaurant } = useApi(getRestaurant);
  const user = useSelector((state) => state.user);
  const { notifications, setNotifications } = useWebSocket(
    "notifications",
    user.userId
  );
  const { request: switchRestaurant } = useApi(putSwitchRestaurant);
  const { request: switchCatering } = useApi(putSwitchCatering);
  const isStripeDone = activationDetails?.isStripeDone ?? null;
  const stripeUrl = activationDetails?.stripeUrl ?? null;
  const canActivateRestaurant = activationDetails?.canActivateRestaurant ?? false;
  const activationLoading = activationDetails?.loading ?? false;
  const firstMissingStep = activationDetails?.missingSteps?.[0] ?? null;
  const firstMissingStepRoute = activationDetails?.firstMissingStepRoute ?? null;
  const navigateToActivationStep = useCallback(() => {
    if (firstMissingStep?.key === "banking" && stripeUrl) {
      window.open(stripeUrl, "_blank");
      return;
    }
    navigate(firstMissingStepRoute || "dashboard/settings");
  }, [firstMissingStep, firstMissingStepRoute, navigate, stripeUrl]);

  useEffect(() => {
    setIsOnOverview(window.location.pathname.endsWith("/dashboard"));
  }, []);

  // Close sidebar when location changes (navigation occurs)
  useEffect(() => {
    // Only close if pathname actually changed (not on initial mount)
    if (prevPathnameRef.current !== location.pathname) {
      onClose();
      prevPathnameRef.current = location.pathname;
    }
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (isOnOverview && isRestaurantAdmin && !isOffoodAdmin && restaurantId) {
      fetchRestaurant(restaurantId).then((response) => {
        const restaurant = response.data;
        setIsRestaurantActive(restaurant.isActive);
        setIsCateringActive(restaurant.isCateringActive);
      });
    }
  }, [
    isRestaurantAdmin,
    isOnOverview,
    isRestaurantActive,
    isCateringActive,
    isOffoodAdmin,
    restaurantId,
    fetchRestaurant,
  ]);

  const handleSwitchRestaurant = () => {
    if (!isRestaurantActive && !canActivateRestaurant) {
      toast.warning(t("dashboard.restaurant.activationIncomplete"));
      navigateToActivationStep();
      return;
    }
    switchRestaurant(restaurantId)
      .then((response) => {
        setIsRestaurantActive(response.data.isActive);
        toast.success(
          t(
            `dashboard.restaurant.toasts.switchRestaurant${
              response.data.isActive ? "Active" : "Inactive"
            }`
          )
        );
      })
      .catch((error) => {
        toast.error(error.message);
        console.error(error);
      });
  };

  const handleSwitchCatering = () => {
    if (!isCateringActive && !canActivateRestaurant) {
      toast.warning(t("dashboard.restaurant.activationIncomplete"));
      navigateToActivationStep();
      return;
    }
    switchCatering(restaurantId)
      .then((response) => {
        setIsCateringActive(response.data.isCateringActive);
        toast.success(
          t(
            `dashboard.restaurant.toasts.switchCatering${
              response.data.isCateringActive ? "Active" : "Inactive"
            }`
          )
        );
      })
      .catch((error) => {
        toast.error(error.message);
        console.error(error);
      });
  };

  const handleStopControllingRestaurant = () => {
    dispatch(stopControllingRestaurant());
    navigate("dashboard");
    onClose();
  };

  const handleStopControllingCompany = () => {
    dispatch(stopControllingCompany());
    navigate("dashboard");
    onClose();
  };

  const handleShowNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showNotifications) setNotifications(0);
  };

  const handleSelectRestaurant = (restaurantId) => {
    dispatch(setRestaurantId(restaurantId));
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const showCateringButton = useCallback(() => {
    if (controlledRestaurantId) {
      controlledConsumptionModes?.find((mode) => mode.consumptionModeId === 5);
    } else {
      const restaurant = restaurants?.find(
        (r) => r.restaurantId === restaurantId
      );
      return restaurant?.consumptionmodes?.find(
        (mode) => mode.consumptionModeId === 5
      );
    }
  }, [
    restaurantId,
    restaurants,
    controlledRestaurantId,
    controlledConsumptionModes,
  ]);

  return (
    <div
      className={`mobile-sidebar-overlay ${className || ""}`}
      onClick={onClose}
    >
      {showNotifications && (
        <div id="notifications" onClick={(e) => e.stopPropagation()}>
          <NotificationsList
            userId={user.userId}
            closeNotifications={() => setShowNotifications(false)}
          />
        </div>
      )}
      {showRestaurantMenu && (
        <div
          id="restaurantmenu"
          className="flex column gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="bold">
            {restaurants.find((r) => r.restaurantId === restaurantId).name}
          </p>
          {restaurants
            .filter((r) => r.restaurantId !== restaurantId)
            .map((restaurant) => (
              <Button
                variant="tertiary"
                className="p-4 justify-start gap-2"
                block
                onClick={() => handleSelectRestaurant(restaurant.restaurantId)}
                key={restaurant.restaurantId}
              >
                <RestaurantAppPreview
                  size="45"
                  img={restaurant.images?.[0]?.fullImagePath}
                  restaurantType={restaurant.restauranttype}
                />
                <span>{restaurant.name}</span>
              </Button>
            ))}
          <Button
            size="sm"
            block
            onClick={() => navigate("register", { state: { initialStep: 2 } })}
          >
            <span>{t("dashboard.restaurant.addRestaurant")}</span>
          </Button>
        </div>
      )}
      <div className="mobile-sidebar" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-sidebar-header">
          <button onClick={onClose} className="close-btn">
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div className="flex gap-1 align-center flex-wrap">
            {isOnOverview &&
              (isRestaurantAdmin ||
                (isOffoodAdmin && !!controlledRestaurantId)) &&
              !activationLoading &&
              (isStripeDone === false ? (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    if (stripeUrl) {
                      window.open(stripeUrl, "_blank");
                    } else {
                      navigate("dashboard/settings/bank");
                    }
                  }}
                >
                  {t(`dashboard.restaurant.stripeNotConnectedShort`)}
                </Button>
              ) : isStripeDone === true ? (
                <>
                  {!isRestaurantActive ? (
                    <Button
                      size="sm"
                      variant={canActivateRestaurant ? "success" : "light"}
                      disabled={!canActivateRestaurant}
                      title={
                        !canActivateRestaurant
                          ? t("dashboard.restaurant.activationIncomplete")
                          : undefined
                      }
                      onClick={() => handleSwitchRestaurant()}
                    >
                      {t("dashboard.restaurant.switchRestaurantActive")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleSwitchRestaurant()}
                    >
                      {t("dashboard.restaurant.switchRestaurantInactive")}
                    </Button>
                  )}
                  {showCateringButton() && (
                    <>
                      {!isCateringActive ? (
                        <Button
                          size="sm"
                          variant={canActivateRestaurant ? "success" : "light"}
                          disabled={!canActivateRestaurant}
                          title={
                            !canActivateRestaurant
                              ? t("dashboard.restaurant.activationIncomplete")
                              : undefined
                          }
                          onClick={() => handleSwitchCatering()}
                        >
                          {t("dashboard.restaurant.switchCateringActive")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleSwitchCatering()}
                        >
                          {t("dashboard.restaurant.switchCateringInactive")}
                        </Button>
                      )}
                    </>
                  )}
                </>
              ) : null)}
            <UserAvatar
              src={user.imagePath}
              initiales={`${user.firstname[0]}${user.lastname[0]}`}
              size="40"
            />
            {isRestaurantAdmin && restaurantId && !controlledRestaurantId && (
              <div
                className="p-1 flex gap-1 align-center bg-pastel-light very-rounded cursor-pointer"
                onClick={() => setShowRestaurantMenu(!showRestaurantMenu)}
              >
                <RestaurantAppPreview
                  isAvatar
                  size="40"
                  img={
                    restaurants.find((r) => r.restaurantId === restaurantId)
                      .images?.[0]?.fullImagePath
                  }
                  restaurantType={
                    restaurants.find((r) => r.restaurantId === restaurantId)
                      .restauranttype
                  }
                />
                <span>
                  {restaurants.find((r) => r.restaurantId === restaurantId).name
                    .length > 12
                    ? `${restaurants
                        .find((r) => r.restaurantId === restaurantId)
                        .name.slice(0, 9)}...`
                    : restaurants.find((r) => r.restaurantId === restaurantId)
                        .name}
                </span>
              </div>
            )}
            <Button
              variant="light"
              size="sm"
              rounded
              onClick={handleShowNotifications}
            >
              <span className="fa-layers fa-fw">
                <FontAwesomeIcon color="black" size="xl" icon={faBell} />
                {notifications > 0 && (
                  <FontAwesomeIcon
                    icon={faCircle}
                    size="xs"
                    color="red"
                    transform="right-10 up-10"
                  />
                )}
              </span>
            </Button>
          </div>
        </div>
        <div className="mobile-sidebar-list">
          {menuList
            .filter((m) => !m.hide)
            .map((item, index) => {
              // Render collapsible item if it has children
              if (item.children) {
                return (
                  <SidebarCollapse
                    key={item.key || index}
                    item={item}
                    isClickable={isClickable}
                  />
                );
              }
              // Wrap item's onClick to also close the sidebar
              const wrappedItem = {
                ...item,
                onClick: item.onClick
                  ? () => {
                      item.onClick();
                      onClose();
                    }
                  : undefined,
              };
              return (
                <SidebarItem
                  className="mobile-sidebar-item"
                  key={item.key || index}
                  item={wrappedItem}
                  isClickable={isClickable}
                />
              );
            })}
          {controlledRestaurantId && isOffoodAdmin && (
            <div className="py-2">
              <Button
                variant="primary"
                onClick={handleStopControllingRestaurant}
              >
                {t("dashboard.admin.clearControlledRestaurant")}
              </Button>
            </div>
          )}
          {controlledCompanyId && isOffoodAdmin && (
            <div className="py-2">
              <Button variant="primary" onClick={handleStopControllingCompany}>
                {t("dashboard.admin.clearControlledCompany")}
              </Button>
            </div>
          )}
          {!isControllingEntity && (
            <div className="py-2">
              <Button
                className="gap-1"
                variant="info"
                size="sm"
                onClick={() => {
                  navigate("support");
                  onClose();
                }}
              >
                <FontAwesomeIcon icon={faCircleQuestion} size="lg" />
                <span>{t("utils.common.support")}</span>
              </Button>
            </div>
          )}
          <LangButton block noFlag={false} />
          {isAuthenticated && !isDemoMode() && (
            <SidebarItem
              className="mobile-sidebar-item"
              item={{
                tKey: "dashboard.logout.",
                onClick: () => {
                  logout();
                  onClose();
                },
              }}
              isClickable={isClickable}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileSidebar;
